import { createSlice, createSelector, createAsyncThunk } from '@reduxjs/toolkit';
import to from 'await-to-js';
import moment from 'moment';
import { toast } from 'react-toastify';
import { RootState } from './store';
import APIService from '../Utils/APIService';

export interface FuelSummary {
  totalFuelFilled: number;
  totalFuelConsumed: number;
  totalFuelCost: number;
  averageFuelUsage: number;
  totalDistanceTraveled: number;
  costPerKm: number;
}

export interface FuelLogRaw {
  id: number;
  vehicleID: string;
  date: string;
  fuelFilled: number;
  fuelUsed: number;
  odometer: number;
  amount: number;
  createdAt: string;
  costPerKm: number;
  remainingFuel: number;
}

export interface FuelState {
  loading: boolean;
  error: boolean;
  fuelRecords: any[];
  logs: any[];
  total: number;
  fuelSummary: FuelSummary;
}
const initialState: FuelState = {
  loading: false,
  error: false,
  fuelRecords: [],
  logs: [],
  total: 0,
  fuelSummary: {
    totalFuelFilled: 0,
    totalFuelConsumed: 0,
    totalFuelCost: 0,
    averageFuelUsage: 0,
    totalDistanceTraveled: 0,
    costPerKm: 0,
  },
};

const calculateFuelSummary = (logs: any[], vehicleID: string, fromDate: string, toDate: string): FuelSummary => {
  let vehicleLogs = logs;

  if (fromDate && toDate) {
    const fromD = new Date(fromDate);
    const toD = new Date(toDate);

    vehicleLogs = vehicleLogs.filter((log) => {
      const logDate = new Date(log.date);
      return logDate >= fromD && logDate <= toD;
    });
  }

  const totalFuelFilled = vehicleLogs.reduce((sum, l) => sum + l.fuelFilled, 0);
  const totalFuelConsumed = vehicleLogs.reduce((sum, l) => sum + l.fuelUsed, 0);
  const totalFuelCost = vehicleLogs.reduce((sum, l) => sum + l.amount, 0);
  const odometers = vehicleLogs.map((l) => l.odometer);
  const totalDistanceTraveled = odometers.length > 1 ? Math.max(...odometers) - Math.min(...odometers) : 0;

  const averageFuelUsage = vehicleLogs.length > 0 ? Number((totalFuelConsumed / vehicleLogs.length).toFixed(2)) : 0;
  const costPerKm = totalDistanceTraveled > 0 ? Number((totalFuelCost / totalDistanceTraveled).toFixed(2)) : 0;

  return {
    totalFuelFilled,
    totalFuelConsumed,
    totalFuelCost,
    averageFuelUsage,
    totalDistanceTraveled,
    costPerKm,
  };
};

export const addFuelRecord = createAsyncThunk<any, any>('fuel/add', async (payload, { rejectWithValue, dispatch }) => {
  const [err, response] = await to(
    APIService.post(`/user/vehicle/${payload.vehicleId}/fuel`, {
      cost: payload.fuelCost,
      date: payload.fuelFillingDate,
      odometer_reading: payload.odometerReading,
      price_per_liter: payload.unitPrice,
      qty: payload.quantity,
      vehicle_id: payload.plateNumber,
    }),
  );
  if (err || !response) {
    toast.error('Failed to add fuel records');
    return rejectWithValue(err);
  }
  toast.success('Fuel records added successfully');
  dispatch(
    getFuelLogs({
      vehicleId: payload.vehicleId,
      fuelEfficiency: payload.fuelEfficiency,
    }),
  );
  return response.data;
});

/* export const updateFuelRecord = createAsyncThunk<any, any>('fuel/update', async (payload, { rejectWithValue }) => {
  const [err, response] = await to(
    APIService.put(
      '/user/log-fuel',
      { request_type: 'update' },
      {
        params: {
          id: payload.id,
          vehicle_id: payload.vehicleNumber,
          date: payload.fuelFillingDate,
          qty: payload.quantity,
          price_per_liter: payload.unitPrice,
          odometer_reading: payload.odometerReading,
          cost: payload.fuelCost,
        },
      },
    ),
  );
  if (err) {
    toast.error('Failed to update fuel records');
    return rejectWithValue(err);
  }
  toast.success('Fuel records updated successfully');
  return response;
}); */

export const getFuelLogs = createAsyncThunk<any, any>('fuel/get', async (payload, { rejectWithValue }) => {
  try {
    const params: Record<string, any> = {
      page: payload.page || 1,
      page_size: payload.pageSize || payload.limit || 20,
    };

    if (payload.fromDate) {
      params.date_from = `${moment(payload.fromDate).utc().startOf('day').format('YYYY-MM-DDTHH:mm:ss')}Z`;
    }
    if (payload.toDate) {
      params.date_to = `${moment(payload.toDate).utc().endOf('day').format('YYYY-MM-DDTHH:mm:ss')}Z`;
    }
    const response = await APIService.get(`/user/vehicle/${payload.vehicleId}/fuel`, { params });
    const body = response.data?.data ?? response.data;
    const rowLogs: any[] = Array.isArray(body) ? body : [];
    const fuelEfficiency = payload.fuelEfficiency || 0;

    const sortedLogs = rowLogs
      .map((log: any) => ({
        id: log.id,
        vehicleID: log.vehicle_id,
        date: log.date,
        fuelFilled: log.qty,
        odometer: log.odometer_reading,
        amount: log.cost,
        createdAt: log.created_at ?? log.date,
      }))
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    const vehiclePrevOdometer: Record<string, number | null> = {};
    const vehicleCumalativeFuel: Record<string, number> = {};

    const fuelLogs = sortedLogs.map((log) => {
      let isFuelInvalid = false;
      const prevOdometer = vehiclePrevOdometer[log.vehicleID] ?? null;
      const distanceTraveled = prevOdometer !== null && log.odometer > prevOdometer ? log.odometer - prevOdometer : 0;
      const fuelUsed =
        distanceTraveled > 0 && fuelEfficiency > 0 ? Number((distanceTraveled / fuelEfficiency).toFixed(2)) : 0;
      const prevCumulative = vehicleCumalativeFuel[log.vehicleID] ?? 0;
      const availableFuel = prevCumulative + log.fuelFilled;
      let cumalativeRemainingFuel = prevCumulative;
      if (availableFuel >= fuelUsed) {
        cumalativeRemainingFuel = Number((availableFuel - fuelUsed).toFixed(2));
      } else {
        isFuelInvalid = true;
      }
      vehicleCumalativeFuel[log.vehicleID] = cumalativeRemainingFuel;
      vehiclePrevOdometer[log.vehicleID] = log.odometer;
      return { ...log, fuelUsed, remainingFuel: cumalativeRemainingFuel, isFuelInvalid };
    });

    return {
      logs: fuelLogs,
      total: response.data?.total ?? fuelLogs.length,
      vehicleID: payload.vehicleId,
      fromDate: payload.fromDate,
      toDate: payload.toDate,
    };
  } catch (err) {
    toast.error('Failed to fetch fuel logs');
    return rejectWithValue(err);
  }
});

const fuelSlice = createSlice({
  name: 'fuel',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(addFuelRecord.pending, (state) => {
        state.loading = true;
        state.error = false;
      })
      .addCase(addFuelRecord.rejected, (state) => {
        state.loading = false;
        state.error = true;
      })
      .addCase(addFuelRecord.fulfilled, (state) => {
        state.loading = false;
      })
      /* .addCase(updateFuelRecord.pending, (state) => {
        state.loading = true;
        state.error = false;
      })
      .addCase(updateFuelRecord.rejected, (state) => {
        state.loading = false;
        state.error = true;
      })
      .addCase(updateFuelRecord.fulfilled, (state, { payload }) => {
        state.loading = false;
        const index = state.fuelRecords.findIndex((record: any) => record.id === payload.id);
        if (index !== -1) {
          state.fuelRecords[index] = payload;
        }
      }) */
      .addCase(getFuelLogs.pending, (state) => {
        state.loading = true;
        state.error = false;
      })
      .addCase(getFuelLogs.rejected, (state) => {
        state.loading = false;
        state.error = true;
      })
      .addCase(getFuelLogs.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.logs = payload.logs;
        state.total = payload.total;
        state.fuelRecords = payload.logs;
        state.fuelSummary = calculateFuelSummary(payload.logs, payload.vehicleID, payload.fromDate, payload.toDate);
      });
  },
});

export const fuelStore = (state: RootState): FuelState => state.fuel;
export const getFuelLoading = createSelector(fuelStore, (s) => s.loading);
export const getFuelSummary = createSelector(fuelStore, (s) => s.fuelSummary);
export default fuelSlice.reducer;
